import { WEI_PER_FLR } from "./constants";

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(Math.max(0, exponent));
}

export function formatAtomic(value: string | bigint, decimals: number, fractionDigits = 2): string {
  const atomic = typeof value === "bigint" ? value : BigInt(value);
  const negative = atomic < 0n;
  const absolute = negative ? -atomic : atomic;
  const safeDecimals = Math.max(0, decimals);
  const safeFractionDigits = Math.max(0, fractionDigits);
  const rounded = safeDecimals > safeFractionDigits
    ? (absolute + powerOfTen(safeDecimals - safeFractionDigits) / 2n) / powerOfTen(safeDecimals - safeFractionDigits)
    : absolute * powerOfTen(safeFractionDigits - safeDecimals);
  const fractionScale = powerOfTen(safeFractionDigits);
  const whole = safeFractionDigits ? rounded / fractionScale : rounded;
  const fraction = safeFractionDigits ? (rounded % fractionScale).toString().padStart(safeFractionDigits, "0") : "";
  const formattedWhole = new Intl.NumberFormat("en-GB").format(whole);
  return `${negative && rounded !== 0n ? "−" : ""}${formattedWhole}${safeFractionDigits ? `.${fraction}` : ""}`;
}

export function formatFlr(value: string | bigint, fractionDigits = 2): string {
  return formatAtomic(value, 18, fractionDigits);
}

export function formatCompactFlr(value: string | bigint): string {
  const flr = Number(typeof value === "bigint" ? value : BigInt(value)) / 1e18;
  return new Intl.NumberFormat("en-GB", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(flr);
}

export function formatToken(value: string | bigint, decimals: number): string {
  return formatAtomic(value, decimals, 2);
}

export function formatUsdFromWei(amountWei: string, priceE18: string | null): string {
  if (!priceE18) return "Unavailable";
  const usdE18 = (BigInt(amountWei) * BigInt(priceE18)) / WEI_PER_FLR;
  return `$${formatAtomic(usdE18, 18, 2)}`;
}

export function formatUsdE18(valueE18: string | null): string {
  return valueE18 === null ? "Unavailable" : `$${formatAtomic(valueE18, 18, 2)}`;
}

export function formatCoverageBps(value: string | null): string {
  if (value === null) return "—";
  return `${formatAtomic(value, 2, 2)}%`;
}

export function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 5) return "now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

export function truncateHash(hash: string, left = 6, right = 4): string {
  return hash.length > left + right + 1 ? `${hash.slice(0, left)}…${hash.slice(-right)}` : hash;
}
